/// <amd-module name="Search/Package/Flux/ActionCreator" />

import * as React from "react";
import * as Constants from "Search/Package/Constants";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { ActionsHub } from "Search/Package/Flux/ActionsHub";
import { IAggregatedState } from "Search/Package/Flux/StoresHub";
import { PackageSearchSource } from "Search/Package/Flux/Sources/PackageSearchSource";
import { Filter, FILTER_CHANGE_EVENT, FILTER_RESET_EVENT, IFilterState } from "SearchUI/Utilities/Filter";
import { TelemetryWriter } from "Search/Package/Flux/Sources/TelemetryWriter";
import { PackageSearchRequest, PackageSearchResponseContent } from "Search/WebApi/Contracts/Search.Shared";
import { ISearchFailedPayload } from "Search/Shared/Base/ActionsHub2";
import { deserialize } from "Search/Shared/Utils";
import { SearchPerfTelemetryAdditionalData } from "Search/Shared/Base/Telemetry";
import { IVssPageContext } from "VSS/Platform/Context";
import { onClickFPS } from "VSS/Platform/FPS";
import { scrubFilters } from "Search/Package/Flux/Stores/FilterStore";
import { SearchSourceType } from "Search/Shared/Base/ActionsHub2";
import { PageSource } from "Search/Shared/Base/Sources/PageSource";
import { SearchStatus } from "Search/Shared/Base/Stores/SearchStore";

export interface ISources {
    searchSource: PackageSearchSource;

    pageSource: PageSource;
}

export class ActionCreator {
    constructor(
        private readonly filter: Filter,
        private readonly telemetryWriter: TelemetryWriter,
        private readonly actionsHub: ActionsHub,
        private readonly sources: ISources,
        private readonly getAggregatedState: () => IAggregatedState,
        private readonly pageContext: IVssPageContext) {
        this.filter.subscribe(this.applyFilter, FILTER_CHANGE_EVENT);
        this.filter.subscribe(this.resetFilters, FILTER_RESET_EVENT);
    }

    public dispose = (): void => {
        this.filter.unsubscribe(this.applyFilter, FILTER_CHANGE_EVENT);
        this.filter.unsubscribe(this.resetFilters, FILTER_RESET_EVENT);
        return;
    }

    public applyFilter = (filterState: IFilterState): void => {
        const currentFilterState = this.filter.getState();
        const { filterStoreState, searchStoreState } = this.getAggregatedState();
        const { filterItems } = filterStoreState;

        const filters: { [key: string]: string[] } = {};
        Object
            .keys(currentFilterState)
            .forEach((key) => {
                const value = currentFilterState[key].value;
                if (!value || value.length === 0) {
                    return;
                }

                if (filterItems[key].enabled) {
                    filters[key] = value;
                }
            });

        // Create a new search query with updated filters.
        const query = {
            ...searchStoreState.query,
            filters: scrubFilters(filters),
            includeFacets: true,
            $skip: 0,
            $top: this.getInitialTake()
        } as PackageSearchRequest;

        this.doSearch(query, false, true, false);
    }

    public onFiltersChanged = (name: string): void => {
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.FiltersUpdated, { name });
    }

    private resetFilters = (): void => {
        this.actionsHub.filterReset.invoke({});
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.FilterResetClicked);
    }

    public toggleFilterPane = (): void => {
        this.actionsHub.filterPaneToggled.invoke(undefined);
    }

    public clickFeedbackLink = (): void => {
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.FeedbackLinkClicked);
    }

    public applySearchText = (searchText: string) => {
        const { searchStoreState } = this.getAggregatedState();
        const searchRequest: PackageSearchRequest = {
            ...searchStoreState.query,
            searchText,
            $skip: 0,
            $top: this.getInitialTake(),
            includeFacets: true
        };

        this.doSearch(searchRequest, false, false, true);
    }

    public openSearchInNewTab = (searchText: string): void => {
        const { searchStoreState } = this.getAggregatedState();
        this.sources.pageSource.navigateToNewSearch(Constants.EntityTypeUrlParam, searchText, searchStoreState.query.filters);
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.SearchInNewTab);
    }

    public performSearch = (
        searchText: string,
        filters: { [id: string]: string[] }): void => {
        const searchQuery = {
            searchText: searchText,
            $skip: 0,
            $top: this.getInitialTake(),
            includeFacets: true,
            filters: filters
        } as PackageSearchRequest;

        this.doSearch(searchQuery);
    }

    public loadInitialState = (
        searchText: string,
        filters?: string,
        result?: string,
        activeTab?: string,
        launchPoint?: string,
        takeResults?: string): void => {
        const request = {
            searchText,
            $skip: 0,
            $top: takeResults && !isNaN(parseInt(takeResults)) ? parseInt(takeResults) : Constants.PackageSearchTakeResults,
            includeFacets: true,
            filters: filters ? deserialize<{ [key: string]: string[] }>(filters) : {}
        } as PackageSearchRequest;

        this.actionsHub.pageInitializationStarted.invoke({ request, activeTabKey: activeTab, launchPoint });
        this.sources
            .searchSource
            .getPackageSearchResults(request, ({ responseWithActivityId, source }) => {
                if (responseWithActivityId) {
                    const packageQueryResponse = responseWithActivityId.content;
                    const activityId = responseWithActivityId.activityId && responseWithActivityId.activityId[0];

                    this.handleResultsLoaded(request, packageQueryResponse, source, activityId);
                }
            }, (error: ISearchFailedPayload<PackageSearchRequest>) => {
                this.actionsHub.searchFailed.invoke({ ...error, query: request });
            });
    }

    /**
    *  Send query on landing page scenario to update searchstore to an adequate state
    */
    public showLandingPage = (): void => {
        const request: PackageSearchRequest = {
            $orderBy: [],
            $skip: 0,
            filters: {},
            includeFacets: true,
            searchText: "",
            $top: this.getInitialTake()
        };

        this.actionsHub.pageInitializationStarted.invoke({ request, isLandingPage: true });
    }

    public notifyResultsRendered = (): void => {
        const { searchStoreState } = this.getAggregatedState();
        const data: SearchPerfTelemetryAdditionalData = {
            responseActivityId: "",//TODO <string>searchStoreState.activityId,
            searchSource: SearchSourceType[searchStoreState.source]
        };

        this.telemetryWriter.notifyResultsRendered(data);
    }

    public publishZeroData = (scenarioType: string, error?: any): void => {
        this.telemetryWriter.publish(
            Constants.CustomerIntelligenceConstants.ZeroData,
            {
                scenarioType,
                error
            });
    }

    public notifyFeedbackMailLinkClicked = (): void => {
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.FeedbackMailLinkClicked);
    }

    public notifySearchFailed = (): void => {
        this.telemetryWriter.notifySearchFailed();
    }

    public clickPackageLink = (link: string, evt: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLAnchorElement>): void => {
        // Instead of redirecting the browser, use Fast past switch to package page.
        onClickFPS(this.pageContext, link, true, evt);
    }

    public fetchMoreResults = (newSkip: number, newTop: number): void => {
        const searchStoreState = this.getAggregatedState().searchStoreState;

        if (searchStoreState.searchStatus !== SearchStatus.Loading) {
            const query = {
                ...searchStoreState.query,
                $skip: newSkip,
                $top: newTop,
                includeFacets: true
            };

            this.doSearch(query, true);
        }
    }

    public publishNotificationBanner = (scenarioType: string): void => {
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.NotificationBanner, { scenarioType });
    }

    public dismissNotificationBanner = (): void => {
        // Add: Error notification banner dismissed telemetry.
        this.actionsHub.errorNotificationBannerDismissed.invoke({});
    }
    private doSearch(
        request: PackageSearchRequest,
        fetchMoreScenario?: boolean,
        filterApplication?: boolean,
        searchTextModified?: boolean): void {
        this.actionsHub.searchStarted.invoke({ request, fetchMoreScenario, filterApplication, searchTextModified });
        this.sources
            .searchSource
            .getPackageSearchResults(request, ({ responseWithActivityId, source }) => {
                if (responseWithActivityId) {
                    const packageRequestResponse = responseWithActivityId.content;
                    const activityId = responseWithActivityId.activityId && responseWithActivityId.activityId[0];

                    this.handleResultsLoaded(request, packageRequestResponse, source, activityId);
                }
            }, (error: ISearchFailedPayload<PackageSearchRequest>) => {
                this.actionsHub.searchFailed.invoke({ ...error, query: request });
            });
    }

    private handleResultsLoaded = (
        request: PackageSearchRequest,
        response: PackageSearchResponseContent,
        source: SearchSourceType,
        activityId: string): void => {
        this.actionsHub.resultsLoaded.invoke({ response, request, activityId, source });
    }

    private getInitialTake = (): number => {
        const infiniteScroll = isFeatureFlagEnabled(this.pageContext, Constants.FeatureFlagConstants.InfiniteScroll, false);
        return infiniteScroll ? Constants.PackageSearchInfiniteScrollTakeResults : Constants.PackageSearchTakeResults;
    }
}